import cx from 'classnames'
import { Slice } from 'prosemirror-model'
import { EditorState, Selection, Transaction } from 'prosemirror-state'
import { EditorView } from 'prosemirror-view'
import {
  parseHTMLStringToFragment,
  serializeFragmentToHTMLString,
  serializeFragmentToPlainTextString,
} from 'proseShared/utils'
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { useQueryClient } from 'react-query'
import { PlaceholderPlugin } from './plugins/PlaceholderPlugin'
import Styles from './ProseInput.module.scss'
import { schema as defaultSchema } from './Schema'
import { ProseInputHandle, ProseInputProps } from './Types'
import { extractMentions, extractPills } from './utils'
import { buildPlugins } from './plugins'
import { useProseStateContext } from './ProseStateContext'
import { nodeViews } from './nodeViews'

export const ProseInput = forwardRef<ProseInputHandle, ProseInputProps>(
  (
    {
      className,
      initialHtmlString = '',
      onChange,
      onFocus,
      onBlur,
      handleKeyDown,
      placeholder,
      plugins: pluginOverrides,
      overrideMentionPromptClassname = '',
      isDisabled = false,
      withBorder = false,
      autoFocus = false,
      schema = defaultSchema,
      ...rest
    },
    forwardedRef,
  ) => {
    const queryClient = useQueryClient()
    const ref = useRef<HTMLDivElement>(null)
    const editorViewRef = useRef<EditorView | null>(null)
    const [isFocused, setIsFocused] = useState(false)

    const context = useProseStateContext()

    useImperativeHandle(forwardedRef, () => ({
      view: () => {
        return editorViewRef.current
      },
      focus: () => {
        editorViewRef.current?.focus()
      },
      blur: () => {
        ref.current?.blur()
      },
      clear: () => {
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const { state } = editorViewRef.current!
        const tx = state.tr.replace(0, state.doc.content.size, Slice.empty)
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        editorViewRef.current!.dispatch(tx)
      },
      replaceHTMLString: (htmlString: string) => {
        if (!editorViewRef.current) return

        const { state } = editorViewRef.current
        const doc = schema.nodes.doc.create({}, parseHTMLStringToFragment(htmlString, schema))
        const newState = EditorState.create({
          schema: state.schema,
          doc,
          plugins: state.plugins,
          selection: Selection.atEnd(doc),
        })
        editorViewRef?.current?.updateState(newState)
        const html = serializeFragmentToHTMLString(newState.doc.content, schema)
        const plainText = serializeFragmentToPlainTextString(newState.doc.content)
        const mentions = extractMentions(newState)
        const pills = extractPills(newState)
        onChange?.(html, plainText, mentions, pills)
      },
    }))

    useEffect(() => {
      if (!ref.current || !queryClient) return
      const plugins = buildPlugins(schema, queryClient, pluginOverrides)
      if (placeholder) {
        plugins.push(PlaceholderPlugin(placeholder))
      }

      const doc = schema.nodes.doc.create({}, parseHTMLStringToFragment(initialHtmlString, schema))
      const targetPos = doc.textContent.length + 1
      const selection = Selection.near(doc.resolve(targetPos), 1)
      const state = EditorState.create({ doc, plugins, selection })

      const editorView = new EditorView(ref.current, {
        attributes: { 'aria-label': rest['aria-label'] || '', role: rest['role'] || '', className: '' },
        state,
        nodeViews,
        editable: () => !isDisabled,
        handleKeyDown,
        handleDOMEvents: {
          focus(view, event) {
            context?.setView(view)
            onFocus?.(event)
            setIsFocused(true)
            return false
          },
          blur(_, event) {
            onBlur?.(event)
            setIsFocused(false)
            return false
          },
        },
        dispatchTransaction(transaction: Transaction) {
          // TODO: Fix this the next time the file is edited.
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          const newState = editorViewRef.current!.state.apply(transaction)
          const html = serializeFragmentToHTMLString(newState.doc.content, schema)
          const plainText = serializeFragmentToPlainTextString(newState.doc.content)
          const mentions = extractMentions(newState)
          const pills = extractPills(newState)
          // TODO: Fix this the next time the file is edited.
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          editorViewRef.current!.updateState(newState)
          context?.updateFromState(newState)
          onChange?.(html, plainText, mentions, pills)
        },
      })

      editorViewRef.current = editorView
      context?.setView(editorView)

      if (autoFocus) setTimeout(() => editorView.focus(), 0)

      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return () => editorViewRef.current!.destroy()
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [context, placeholder, isDisabled, queryClient])

    return (
      <>
        <div
          className={cx(className, 'prose-input', Styles.proseInput, {
            [Styles.disabled]: isDisabled,
            [Styles.isFocused]: isFocused,
            [Styles.withBorder]: withBorder,
          })}
          ref={ref}
        />
      </>
    )
  },
)
